import '../demand-line-item-evidence-dialog.css';
import '@kerfjs/ui/nav-stack.css';
import '@kerfjs/ui/split-view.css';

import { Button } from '@kerfjs/ui/button';
import { remify } from '@kerfjs/ui/css-values';
import { deviceClass } from '@kerfjs/ui/device-class';
import { List } from '@kerfjs/ui/list';
import { Pane } from '@kerfjs/ui/pane';
import { SplitView } from '@kerfjs/ui/split-view';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import type { SafeHtml } from 'kerfjs';

import { lineReviewFindings } from '../../../shared/quotation-review.js';
import type { AnalysisReport, QuoteMatrix } from '../../data/api.js';
import {
  type IntakeLine,
  lineBenchmarkDialogOpen,
  lineEvidenceDetailActive,
  lineEvidenceView,
} from '../../data/state.js';
import { EvidenceBenchmarkDialog } from './components/evidence-benchmark-dialog.js';
import { EvidenceBenchmarks } from './components/evidence-benchmarks.js';
import { EvidenceDetailActions } from './components/evidence-detail-actions.js';
import { EvidenceDetailToolbar } from './components/evidence-detail-toolbar.js';
import { EvidencePricing } from './components/evidence-pricing.js';
import { EvidenceQuotes } from './components/evidence-quotes.js';
import { EvidenceSectionList } from './components/evidence-section-list.js';
import { evidenceTitles } from './components/evidence-sections.js';

export function DemandLineItemEvidenceDialog({
  line,
  report,
  matrix,
  lightDismiss = false,
}: {
  line: IntakeLine;
  report: AnalysisReport;
  matrix: QuoteMatrix | null;
  lightDismiss?: boolean;
}): SafeHtml {
  const verdict = report.verdicts.find((row) => row.lineItemId === line.id);
  if (verdict === undefined)
    return (
      <DialogSurface
        label={`Line ${String(line.lineNumber)} Evidence`}
        className="demand-line-item-evidence-dialog"
        lightDismiss={lightDismiss}
        preferredWidth={remify(1200)}
        viewportGutter={remify(12)}
        rootAttributes={{ 'data-auto-open-dialog': '' }}
        footer={
          <Button
            tone="neutral"
            emphasis="primary"
            size="default"
            action="evidence-close"
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Close
          </Button>
        }
      >
        <p>Evidence is no longer available for this analysis version.</p>
      </DialogSurface>
    );

  const base = report.analysis.summary?.base ?? matrix?.base ?? 'PKR';
  const reviewFindings = lineReviewFindings(report.analysis.summary?.review, line.id);
  const active = lineEvidenceView.value;
  const compact = deviceClass().value.compact;
  const detailActions = active === 'benchmarks' ? <EvidenceDetailActions /> : undefined;
  let activeBody: SafeHtml;
  switch (active) {
    case 'pricing':
      activeBody = <EvidencePricing verdict={verdict} base={base} />;
      break;
    case 'quotes':
      activeBody = (
        <EvidenceQuotes line={line} matrix={matrix} base={base} reviewFindings={reviewFindings} compact={compact} />
      );
      break;
    case 'benchmarks':
      activeBody = <EvidenceBenchmarks report={report} lineVerdictId={verdict.id} base={base} />;
      break;
  }
  const detailBody = (
    <List gap="m" controlInsets="trbl">
      {activeBody}
    </List>
  );
  const detail = compact ? (
    <Pane label="Evidence Detail">{detailBody}</Pane>
  ) : (
    <Pane label="Evidence Detail" header={<EvidenceDetailToolbar active={active} actions={detailActions} />}>
      {detailBody}
    </Pane>
  );

  return (
    <>
      <DialogSurface
        label={`Line ${String(line.lineNumber)} · ${line.normalizedDescription ?? line.description}`}
        className="demand-line-item-evidence-dialog"
        lightDismiss={lightDismiss}
        preferredWidth={remify(1200)}
        viewportGutter={remify(12)}
        rootAttributes={{ 'data-auto-open-dialog': '' }}
        footer={
          <Button
            tone="neutral"
            size="default"
            emphasis="plain"
            action="evidence-close"
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Close
          </Button>
        }
      >
        <div class="line-evidence">
          <SplitView
            id="line-evidence-split"
            label="Pricing Evidence"
            list={<EvidenceSectionList active={active} />}
            detail={detail}
            listTitle="Evidence"
            detailTitle={evidenceTitles[active]}
            compact={compact}
            detailActive={lineEvidenceDetailActive.value}
            compactStack={{ detail: { toolbar: active === 'benchmarks' ? { trailing: detailActions } : undefined } }}
            className="line-evidence-split"
          />
        </div>
      </DialogSurface>
      {lineBenchmarkDialogOpen.value ? (
        <EvidenceBenchmarkDialog line={line} base={base} compact={compact} lightDismiss={lightDismiss} />
      ) : null}
    </>
  );
}
